import styled from 'styled-components';
import { Button, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DataCardHeader } from 'components/styled/StyledDashboardComponents';

// Main container styles
export const PatientOverviewContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

export const PatientOverviewContent = styled.div.withConfig({
  shouldForwardProp: (prop: string) => prop !== 'breakpointBreached',
}) <{ breakpointBreached: boolean }>`
  display: flex;
  flex-flow: column;
  flex-wrap: wrap;
  gap: ${({ breakpointBreached }) => breakpointBreached ? '1rem' : '2rem'};
  justify-content: center;
  align-items: center;
  grid-auto-rows: max-content;
`;

// Grid layout for main sections
export const PatientOverviewGrid = styled.div.withConfig({
  shouldForwardProp: (prop: string) => prop !== 'breakpointBreached',
}) <{ breakpointBreached: boolean }>`
  display: ${({ breakpointBreached }) => breakpointBreached ? '' : 'grid'};
  grid-template-columns: ${({ breakpointBreached }) => breakpointBreached ? '1fr' : '2fr 4fr 1fr'};
  gap: 2rem;
  align-items: start;
  width: 100%;
`;

export const BackButton = styled(Button)`
  color: #225E9F;
  margin: 0;
  text-decoration: underline;
  font-size: 0.875rem;
  padding: 0.25rem 0.5rem;
  min-width: auto;
  text-wrap: nowrap;
`;

export const NavigationContainer = styled.div`
  justify-self: start;
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

export const ChevronIcon = styled(FontAwesomeIcon)`
  font-size: 0.75rem;
`;

export const PatientNameButton = styled(Button)`
  color: #225E9F !important;
  margin: 0;
  font-size: 0.875rem;
  padding: 0.25rem 0.5rem;
  min-width: auto;
`;

export const ExamRoomIcon = styled(FontAwesomeIcon)`
  margin-right: 10px;
`;

export const DataTableItemText = styled(Typography)`
  font-size: 1rem;
`;

// Surface card styles
export const PatientOverviewCard = styled.div.withConfig({
  shouldForwardProp: (prop: string) => prop !== 'breakpointBreached',
}) <{ breakpointBreached: boolean }>`
  width:  ${({ breakpointBreached }) => breakpointBreached ? '100%' : '700px'};
  height: ${({ breakpointBreached }) => breakpointBreached ? '100%' : '158px'};
  padding: ${({ breakpointBreached }) => breakpointBreached ? '20px' : '1.5rem 2rem'};
  margin-bottom: ${({ breakpointBreached }) => breakpointBreached ? '2rem' : '0'};
  display: flex;
  align-items: center;
  border-radius: 8px;
  background: #F1F3F5;
  justify-content: 'center';
  box-shadow: 2px 2px 9.4px 0 rgba(0, 0, 0, 0.25);
`;

// Surface content grid
export const PatientOverviewCardContent = styled.div.withConfig({
  shouldForwardProp: (prop: string) => prop !== 'breakpointBreached',
}) <{ breakpointBreached: boolean }>`
  display: ${({ breakpointBreached }) => breakpointBreached ? '' : 'grid'};
  grid-template-columns: ${({ breakpointBreached }) => breakpointBreached ? '1fr' : '0.9fr 0.1fr'};
  flex-direction: row;
  align-items: center;
  width: 100%;
  gap: 1rem;
`;

export const PatientNameHeader = styled(DataCardHeader)`
  display: flex;
  align-items: center;  
  font-size: 1.5rem;
  text-align: center;
`;

export const PatientHeaderGrid = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  padding-left: 2rem;
  padding-right: 2rem;
  padding-bottom: 0.5rem;
`;

export const PatientNameContainer = styled.div`
  display: flex;
  align-items: center;
`;

export const LoadingContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0 2rem;
`;

export const LoadingBoxLarge = styled.div.withConfig({
  shouldForwardProp: (prop: string) => prop !== 'sx',
}) <{ sx?: any }>`
  width: 90%;
  height: 2.35rem;
  border-radius: 4px;
`;

export const LoadingBoxSmall = styled.div.withConfig({
  shouldForwardProp: (prop: string) => prop !== 'sx',
}) <{ sx?: any }>`
  width: 100%;
  height: 1rem;
  border-radius: 4px;
`;

// Loading skeleton components
export const LoadingHeaderContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0 2rem;
`;

export const LoadingDetailsContainer = styled.div`
  display: flex;
  grid-template-columns: 1fr 1fr;
  width: 100%;
  padding-top: 1rem;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`;

export const LoadingSkeletonBox = styled.div.withConfig({
  shouldForwardProp: (prop: string) => prop !== 'sx',
}) <{ sx?: any }>`
  width: 100%;
  height: 2rem;
  border-radius: 4px;
`;

// Patient details section
export const PatientDetailsContainer = styled.div`
  padding-left: 2rem;
  padding-right: 2rem;
`;

export const PatientDetailRow = styled.div.withConfig({
  shouldForwardProp: (prop: string) => prop !== 'flexCenter',
}) <{ flexCenter?: boolean }>`
  display: ${({ flexCenter }) => flexCenter ? 'flex' : 'grid'};
  grid-template-columns: 1fr 1fr;
  width: 100%;
  padding-left: 3rem;
  align-items: center;
  justify-content: left;
  gap: 0.5rem;
`;

export const PatientDetailText = styled.div.withConfig({
  shouldForwardProp: (prop: string) => prop !== 'leftAligned',
}) <{ leftAligned?: boolean }>`
  line-height: unset;
  padding: 0 0.25rem;
  font-size: 1rem;
  text-align: ${({ leftAligned }) => leftAligned ? 'left' : 'center'};
  padding-left: ${({ leftAligned }) => leftAligned ? '20px' : '0'};
`;

export const PatientDetailLabelBold = styled.span`
  font-weight: 800;
`;

export const PatientDetailLabelMedium = styled.span`
  font-weight: 600;
`;

// Action buttons section
export const HeaderButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
`;

export const EditInfoButton = styled(Button)`
  display: flex;
  flex-wrap: nowrap;
  color: #225E9F;
`;

export const NewExamButton = styled(Button)`
  display: flex;
  flex-wrap: nowrap;
  white-space: nowrap;
  gap: 1rem;
  color: #ffffff !important;
  margin-top: 1rem;
  
  /* Override Material-UI button text color */
  &.MuiButton-root,
  &.MuiButton-contained,
  .MuiButton-label,
  .MuiTypography-root {
    color: #ffffff !important;
  }
  
  /* Target the button's internal span elements */
  span {
    color: #ffffff !important;
  }
`;

export const LoadingItem = styled.div.withConfig({
  shouldForwardProp: (prop: string) => prop !== 'sx',
}) <{ sx?: any }>`
  width: 100%;
  height: 2rem;
  border-radius: 4px;
`;

// Exam history section
export const ExamHistoryContainer = styled.div.withConfig({
  shouldForwardProp: (prop: string) => prop !== 'breakpointBreached',
}) <{ breakpointBreached: boolean }>`
  width: 100%;
  margin-top: ${({ breakpointBreached }) => breakpointBreached ? '2rem' : '0'};
  flex-direction: column;
`;

export const ExamHistoryHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

// Exam room button icon
export const ExamRoomButtonIcon = styled(FontAwesomeIcon)`
  margin-right: 10px;
`;

// Responsive breakpoint adjustments
export const ResponsiveContainer = styled.div`
  @media (max-width: 1200px) {
    ${PatientOverviewContent} {
      gap: 1rem;
    }

    ${PatientOverviewCard} {
      padding: 40px;
      margin-bottom: 2rem;
    }

    ${ExamHistoryContainer} {
      margin-top: 2rem;
    }
  }
`;
